import React, { useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * ConfirmDialog — ยืนยันการทำงาน รองรับการบังคับกรอกเหตุผล
 *
 * ใช้ที่: ปรับคะแนนด้วยมือ (FR-ADM-04) · ระงับบัญชี (FR-ADM-07) ·
 * ปฏิเสธคำขอเปลี่ยนประเภทสมาชิก (FR-ADM-09) · Bulk Approve (FR-ADM-13)
 *
 * เหตุผลที่กรอกถูกบันทึกลง Audit Log ทุกครั้ง (FR-ADM-37) — ข้อความจึงต้องอ่านย้อนหลังรู้เรื่อง
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** อธิบายผลที่จะเกิด เป็นข้อความหรือ node ก็ได้ — บังคับ */
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** true = ต้องกรอกเหตุผลก่อนยืนยันได้ */
  requireReason?: boolean;
  /** เหตุผลสำเร็จรูปให้เลือกเพื่อลดการพิมพ์ */
  reasonPresets?: string[];
  reasonLabel?: string;
  minReasonLength?: number;
  busy?: boolean;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, description, confirmLabel = 'ยืนยัน', cancelLabel = 'ยกเลิก',
  tone = 'default', requireReason = false, reasonPresets, reasonLabel = 'เหตุผล',
  minReasonLength = 10, busy = false, onConfirm, onCancel,
}) => {
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  /** เก็บไว้ใน ref ด้วยเหตุผลเดียวกับ SideDrawer — ผู้เรียกส่งฟังก์ชันเขียนสด
      ถ้าใส่ใน dependency แล้ว parent re-render เมื่อไร โฟกัสจะกระเด็นทันที */
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) { setReason(''); return; }
    const el = ref.current;
    // หา [data-autofocus] แยกก่อน — querySelector หลาย selector คืนตัวแรก
    // ตามลำดับในเอกสาร ไม่ใช่ตัวแรกที่ตรงกับ selector ตัวแรก
    const target = el?.querySelector<HTMLElement>('[data-autofocus]')
      ?? el?.querySelector<HTMLElement>('input, textarea, button');
    target?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) { e.stopPropagation(); onCancelRef.current(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
    // ตั้งใจไม่ใส่ onCancel — อ่านผ่าน onCancelRef
  }, [open, busy]);

  if (!open) return null;
  const ok = !requireReason || reason.trim().length >= minReasonLength;

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[var(--scrim)] p-[var(--space-4)]">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="flex w-full max-w-[520px] flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] bg-[var(--surface-0)] p-[var(--space-5)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-modal)]"
      >
        <div className="flex items-start gap-[var(--space-25)]">
          <Icon
            name={tone === 'danger' ? 'report' : 'help'}
            size={24}
            filled
            className={tone === 'danger' ? 'text-[var(--error-text)]' : 'text-[var(--blue-700)]'}
          />
          <h2 id="confirm-title" className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
            {title}
          </h2>
        </div>

        <div className="text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{description}</div>

        {requireReason && (
          <div className="flex flex-col gap-[var(--space-2)]">
            {reasonPresets && reasonPresets.length > 0 && (
              <div className="flex flex-wrap gap-[var(--space-1)]">
                {reasonPresets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setReason(p)}
                    className={cn(
                      'min-h-[var(--hit-table)] rounded-[var(--radius-pill)] border px-[var(--space-25)] text-[length:var(--text-table-sm)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2',
                      reason === p
                        ? 'border-[var(--blue-700)] bg-[var(--blue-100)] text-[var(--blue-900)]'
                        : 'border-[var(--line-300)] bg-[var(--surface-0)] text-[var(--ink-700)] hover:bg-[var(--blue-050)]',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            <label className="flex flex-col gap-[var(--space-1)]">
              <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">
                {reasonLabel} <span className="text-[var(--error-text)]">จำเป็น</span>
              </span>
              <textarea
                data-autofocus
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-describedby="confirm-reason-hint"
                className="rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              />
              <span id="confirm-reason-hint" className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
                เหตุผลนี้จะถูกบันทึกใน Audit Log — ต้องอย่างน้อย {minReasonLength} ตัวอักษร (พิมพ์แล้ว {reason.trim().length})
              </span>
            </label>
          </div>
        )}

        <div className="flex items-center justify-end gap-[var(--space-2)]">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(requireReason ? reason.trim() : undefined)}
            disabled={!ok || busy}
            className={cn(
              'min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] px-[var(--space-5)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2',
              tone === 'danger' ? 'bg-[var(--error-solid)] hover:bg-[var(--error-text)]' : 'bg-[var(--blue-700)] hover:bg-[var(--blue-800)]',
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
